import { useCallback, useState } from 'react'
import { sumBy } from 'lodash'
import { Selector } from '@Components/selector/selector'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { BarcodeFormat, StepProps } from '../../types'
import { getFieldEnum } from '../../schema-utils'
import { formatPrizeDisplay } from '../../format-utils'
import { GameTypeHelpModal } from './game-type-help-modal'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'

import './tickets-step.scss'

const CLASS_NAME = 'tickets-step'
const PRIZE_DIGIT_CAP = 12

type TicketEntry = {
  prizeAmount: string
  gameType: string
  barcodeNumber: string
  selectedFormatIndex: number
}

type StoredTicket = {
  prizeAmount?: string | number
  gameType?: string
  barcodeNumber?: string
  selectedFormatIndex?: number
}

function emptyTicket(): TicketEntry {
  return { prizeAmount: '', gameType: '', barcodeNumber: '', selectedFormatIndex: 0 }
}

/**
 * Auto-format a barcode value based on the placeholder template.
 * The placeholder defines the shape (e.g., "1748-021474054-175190").
 * Digits are extracted from input and dashes inserted at positions
 * where the placeholder has a dash.
 */
function formatBarcode(value: string, placeholder: string): string {
  const digits = value.replace(/\D/g, '')
  let result = ''
  let digitIndex = 0

  for (let i = 0; i < placeholder.length && digitIndex < digits.length; i++) {
    if (placeholder[i] === '-') {
      result += '-'
    } else {
      result += digits[digitIndex]
      digitIndex++
    }
  }

  return result
}

export const TicketsStep = ({
  schema,
  formData,
  onSubmit,
  onBack,
  claimReservationId,
}: StepProps) => {
  const existingTickets = ((formData.tickets ?? []) as StoredTicket[]).map((t) => ({
    prizeAmount: String(t.prizeAmount ?? ''),
    gameType: t.gameType ?? '',
    barcodeNumber: t.barcodeNumber ?? '',
    selectedFormatIndex: t.selectedFormatIndex ?? 0,
  }))
  const existingPaymentMethod = (formData.paymentMethod ?? '') as string

  const [numberOfTickets, setNumberOfTickets] = useState<number>(
    existingTickets.length > 0 ? existingTickets.length : 1,
  )
  const [tickets, setTickets] = useState<TicketEntry[]>(
    existingTickets.length > 0 ? existingTickets : [emptyTicket()],
  )
  const [paymentMethod, setPaymentMethod] = useState<string>(existingPaymentMethod)
  const [showGameTypeHelp, setShowGameTypeHelp] = useState(false)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const paymentMethodOptions =
    getFieldEnum(schema, '', 'paymentMethod') ?? schema.properties.paymentMethod?.enum ?? []
  const paymentMethodMaxPrize = schema.properties.paymentMethod?.['x-enum-max-prize-amount'] ?? {}
  const ticketProperties = schema.properties.tickets?.items?.properties ?? {}
  const gameTypeOptions = ticketProperties.gameType?.enum ?? []
  const barcodeFormats = schema.barcodeFormats ?? {}
  const getBarcodeFormat = (gameType: string): BarcodeFormat | BarcodeFormat[] | undefined =>
    barcodeFormats[gameType]
  const isMultiFormat = (gameType: string): boolean => Array.isArray(getBarcodeFormat(gameType))
  const getFirstFormat = (gameType: string): BarcodeFormat | undefined => {
    const format = getBarcodeFormat(gameType)
    if (Array.isArray(format)) {
      return format[0]
    }
    return format
  }

  const hasPaymentMethod = 'paymentMethod' in schema.properties
  const hasGameType = 'gameType' in ticketProperties
  const hasPrizeAmount = 'prizeAmount' in ticketProperties
  const hasBarcodeNumber = 'barcodeNumber' in ticketProperties

  const ticketRequired = schema.properties.tickets?.items?.required ?? []
  const isGameTypeRequired = ticketRequired.includes('gameType')
  const isPrizeAmountRequired = ticketRequired.includes('prizeAmount')
  const isBarcodeRequired = ticketRequired.includes('barcodeNumber')

  const isBarcodeValid = (ticket: TicketEntry): boolean => {
    if (!hasBarcodeNumber || !isBarcodeRequired) {
      return true
    }
    if (ticket.barcodeNumber === '') {
      return false
    }
    const format = getBarcodeFormat(ticket.gameType)
    if (Array.isArray(format)) {
      const selected = format[ticket.selectedFormatIndex]
      return selected ? new RegExp(selected.pattern).test(ticket.barcodeNumber) : false
    }
    if (format?.pattern) {
      return new RegExp(format.pattern).test(ticket.barcodeNumber)
    }
    return true
  }

  const handleNumberOfTicketsChange = useCallback((value: number) => {
    setNumberOfTickets(value)
    setTickets((prev) => {
      if (value > prev.length) {
        const additional = Array.from({ length: value - prev.length }, () => emptyTicket())
        return [...prev, ...additional]
      }
      return prev.slice(0, value)
    })
  }, [])

  const updateTicket = useCallback(
    (index: number, field: keyof TicketEntry, value: string | number) => {
      setTickets((prev) => {
        const updated = [...prev]
        updated[index] = { ...updated[index], [field]: value }
        return updated
      })
    },
    [],
  )

  const paymentMethodCap = paymentMethodMaxPrize[paymentMethod]

  const handlePrizeChange = useCallback(
    (index: number, raw: string) => {
      let cleaned = raw.replace(/[^0-9.]/g, '')
      const firstDot = cleaned.indexOf('.')
      if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
        if (cleaned.length - firstDot - 1 > 2) {
          cleaned = cleaned.slice(0, firstDot + 3)
        }
      }
      const digitCount = cleaned.replace(/\./g, '').length
      if (digitCount > PRIZE_DIGIT_CAP) {
        return
      }
      if (paymentMethodCap !== undefined && cleaned !== '' && cleaned !== '.') {
        const numeric = Number(cleaned)
        if (Number.isFinite(numeric)) {
          const otherTotal = sumBy(
            tickets.filter((_, i) => i !== index),
            (t) => Number(t.prizeAmount) || 0,
          )
          const remaining = paymentMethodCap - otherTotal
          if (numeric > remaining) {
            cleaned = String(Math.max(0, remaining))
          }
        }
      }
      updateTicket(index, 'prizeAmount', cleaned)
    },
    [paymentMethodCap, tickets, updateTicket],
  )

  const totalPrizeAmount = sumBy(tickets, (t) => Number(t.prizeAmount) || 0)
  const exceedsPaymentMethodCap =
    paymentMethodCap !== undefined && totalPrizeAmount > paymentMethodCap

  const isValid =
    (!hasPaymentMethod || paymentMethod !== '') &&
    !exceedsPaymentMethodCap &&
    tickets.every(
      (t) =>
        (!hasGameType || !isGameTypeRequired || t.gameType !== '') &&
        isBarcodeValid(t) &&
        (!hasPrizeAmount || !isPrizeAmountRequired || t.prizeAmount !== ''),
    )

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      setAttemptedSubmit(true)
      return
    }

    const formattedTickets = tickets.map((t) => ({
      prizeAmount: Number(t.prizeAmount),
      gameType: t.gameType,
      barcodeNumber: t.barcodeNumber,
    }))

    const data: Record<string, unknown> = { tickets: formattedTickets }
    if (hasPaymentMethod) {
      data.paymentMethod = paymentMethod
    }

    onSubmit(data)
  }, [isValid, tickets, paymentMethod, hasPaymentMethod, onSubmit])

  return (
    <section className={CLASS_NAME}>
      <h2 className={`${CLASS_NAME}__section-title`}>Ticket Details</h2>
      <p className={`${CLASS_NAME}__disclaimer`}>Altering tickets subject to 5 years in prison.</p>

      <section className={`${CLASS_NAME}__field`}>
        <label className={`${CLASS_NAME}__label`}>Number of Tickets</label>
        {claimReservationId ? (
          <p className={`${CLASS_NAME}__static-value`}>{numberOfTickets}</p>
        ) : (
          <Selector<number>
            ariaLabel="Number of Tickets"
            placeHolderText=""
            value={`${numberOfTickets}`}
            selectables={Array.from({ length: 15 }, (_, i) => ({
              value: i + 1,
              label: `${i + 1}`,
            }))}
            onValueChange={handleNumberOfTicketsChange}
          />
        )}
      </section>

      {hasPaymentMethod && (
        <section className={`${CLASS_NAME}__field`}>
          <label className={`${CLASS_NAME}__label`}>Payment Method *</label>
          <Selector<string>
            ariaLabel="Payment Method"
            placeHolderText="Select Payment Method..."
            value={paymentMethod || undefined}
            selectables={paymentMethodOptions.map((opt) => ({
              value: opt,
              label: opt,
            }))}
            onValueChange={setPaymentMethod}
          />
          {attemptedSubmit && paymentMethod === '' && (
            <span className={`${CLASS_NAME}__field-error`}>Payment Method is required</span>
          )}
        </section>
      )}

      {tickets.map((ticket, index) => (
        <section key={index} className={`${CLASS_NAME}__ticket`}>
          {numberOfTickets > 1 && (
            <h3 className={`${CLASS_NAME}__ticket-header`}>
              Ticket {index + 1} of {numberOfTickets}
            </h3>
          )}

          {hasPrizeAmount && (
            <section className={`${CLASS_NAME}__field`}>
              <label className={`${CLASS_NAME}__label`}>
                Prize Amount{isPrizeAmountRequired ? ' *' : ''}
              </label>
              <section className={`${CLASS_NAME}__prize-input-wrapper`}>
                <span className={`${CLASS_NAME}__prize-prefix`}>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={`Prize Amount for Ticket ${index + 1}`}
                  value={formatPrizeDisplay(ticket.prizeAmount)}
                  placeholder="Enter Prize Amount, e.g. 600"
                  onChange={(e) => handlePrizeChange(index, e.target.value)}
                  className={`${CLASS_NAME}__input-control ${CLASS_NAME}__prize-input`}
                />
              </section>
              {exceedsPaymentMethodCap && (
                <span className={`${CLASS_NAME}__field-error`}>
                  {paymentMethod} is not available for prizes over $
                  {paymentMethodCap.toLocaleString()}
                </span>
              )}
              {attemptedSubmit && isPrizeAmountRequired && ticket.prizeAmount === '' && (
                <span className={`${CLASS_NAME}__field-error`}>Prize Amount is required</span>
              )}
            </section>
          )}

          {hasGameType && (
            <section className={`${CLASS_NAME}__field`}>
              <label className={`${CLASS_NAME}__game-type-label ${CLASS_NAME}__label`}>
                Game Type{isGameTypeRequired ? ' *' : ''}
                <button
                  type="button"
                  className={`${CLASS_NAME}__info-button`}
                  onClick={() => setShowGameTypeHelp(true)}
                  aria-label="Game Type Help">
                  ?
                </button>
              </label>
              <Selector<string>
                ariaLabel={`Game Type for Ticket ${index + 1}`}
                placeHolderText="Select Game Type..."
                value={ticket.gameType || undefined}
                selectables={gameTypeOptions.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
                onValueChange={(val) => {
                  updateTicket(index, 'gameType', val)
                  updateTicket(index, 'barcodeNumber', '')
                  updateTicket(index, 'selectedFormatIndex', 0)
                }}
              />
              {attemptedSubmit && isGameTypeRequired && ticket.gameType === '' && (
                <span className={`${CLASS_NAME}__field-error`}>Game Type is required</span>
              )}
            </section>
          )}

          {hasBarcodeNumber && (!hasGameType || ticket.gameType) && (
            <section className={`${CLASS_NAME}__field`}>
              <label className={`${CLASS_NAME}__label`}>
                Barcode Number{isBarcodeRequired ? ' *' : ''}
              </label>
              {isMultiFormat(ticket.gameType) ? (
                <section className={`${CLASS_NAME}__barcode-formats`}>
                  <p className={`${CLASS_NAME}__barcode-format-hint`}>
                    Fill out the field that matches your ticket&apos;s barcode pattern.
                  </p>
                  {(getBarcodeFormat(ticket.gameType) as BarcodeFormat[]).map((f, fIdx) => {
                    const isSelected = ticket.selectedFormatIndex === fIdx
                    return (
                      <label key={fIdx} className={`${CLASS_NAME}__barcode-format-row`}>
                        <input
                          type="radio"
                          name={`barcode-format-${index}`}
                          checked={isSelected}
                          onChange={() => {
                            updateTicket(index, 'selectedFormatIndex', fIdx)
                            updateTicket(index, 'barcodeNumber', '')
                          }}
                          className={`${CLASS_NAME}__barcode-format-radio`}
                        />
                        {isSelected ? (
                          <input
                            type="text"
                            autoFocus
                            aria-label={`Barcode Number for Ticket ${index + 1}`}
                            value={ticket.barcodeNumber}
                            placeholder={f.placeholder}
                            onChange={(e) => {
                              const formatted = formatBarcode(e.target.value, f.placeholder)
                              updateTicket(index, 'barcodeNumber', formatted)
                            }}
                            maxLength={f.placeholder.length}
                            className={`${CLASS_NAME}__barcode-format-input`}
                          />
                        ) : (
                          <span className={`${CLASS_NAME}__barcode-format-label`}>
                            {f.placeholder}
                          </span>
                        )}
                      </label>
                    )
                  })}
                </section>
              ) : (
                <input
                  type="text"
                  aria-label={`Barcode Number for Ticket ${index + 1}`}
                  value={ticket.barcodeNumber}
                  placeholder={
                    getFirstFormat(ticket.gameType)?.placeholder ?? 'Enter Barcode Number'
                  }
                  onChange={(e) => {
                    const format = getFirstFormat(ticket.gameType)
                    const formatted = format?.placeholder
                      ? formatBarcode(e.target.value, format.placeholder)
                      : e.target.value
                    updateTicket(index, 'barcodeNumber', formatted)
                  }}
                  maxLength={getFirstFormat(ticket.gameType)?.placeholder?.length}
                  className={`${CLASS_NAME}__input-control`}
                />
              )}
              {attemptedSubmit && !isBarcodeValid(ticket) && (
                <span className={`${CLASS_NAME}__field-error`}>
                  {ticket.barcodeNumber === ''
                    ? 'Barcode Number is required'
                    : 'Barcode Number does not match the expected format'}
                </span>
              )}
            </section>
          )}
        </section>
      ))}

      <GameTypeHelpModal open={showGameTypeHelp} onClose={() => setShowGameTypeHelp(false)} />

      <FormFloatingFooter>
        <section className="floating-footer__buttons">
          <Button className="floating-footer__next-button" onClick={handleSubmit}>
            Next
          </Button>
          <Button className="floating-footer__previous-button" onClick={onBack}>
            {' '}
            <img src={PreviousChevron} alt="previous-icon" />
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
